import ContactAvatar from './ContactAvatar.tsx';
import { useContactAttendanceSummary } from './Chat/useContactAttendanceSummary.ts';
import React, { useEffect, useState } from 'react';
import {
  X,
  Clock,
  CheckCircle2,
  StickyNote,
  Activity,
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext.tsx';
import LeadTimeline from './LeadTimeline.tsx';
import ContactFlowControl from './ContactFlowControl.tsx';
import SidebarTasksList from './SidebarTasksList.tsx';
import SidebarNotesList from './SidebarNotesList.tsx';
import LeadActionsBar, { FunnelStage } from './lead-actions/LeadActionsBar.tsx';
import EditLeadModal from './lead-actions/EditLeadModal.tsx';
import CreateLeadModal from './lead-actions/CreateLeadModal.tsx';
import CreateAppointmentModal from './lead-actions/CreateAppointmentModal.tsx';
import AttendanceModal from './lead-actions/AttendanceModal.tsx';
import NoShowModal from './lead-actions/NoShowModal.tsx';
import RescheduleModal from './lead-actions/RescheduleModal.tsx';
import SaleModal from './lead-actions/SaleModal.tsx';
import ContactTagsPanel from './ContactTagsPanel.tsx';
import ContactModeControl from './Chat/ContactModeControl.tsx';
import { Lead } from '../services/api.ts';
import type { ContactModeResponse } from '../services/api.ts';
import { TagResponse } from '../services/apiTags.ts';

export type ContactSidebarTab = 'timeline' | 'tasks' | 'notes' | 'flows';

interface FunnelStatus {
  agendamento_id?: number;
  agendamento_consulta_data?: string;
  comparecimento_id?: number;
  venda_id?: number;
  no_show_id?: number;
  lead_id?: number;
  paciente_id?: number;
}

interface ContactSidebarProps {
  flowsEnabled?: boolean;
  isOpen: boolean;
  attendanceEnabled?: boolean;
  onClose: () => void;

  contactId: number;
  contactPhone: string;
  contactName: string;
  contactPhoto?: string;
  contactSourceId?: string;
  clinicId: number;
  humanMode: boolean;
  identityResolved: boolean;
  onModeConfirmed: (response: ContactModeResponse) => boolean | void;

  initialTab?: ContactSidebarTab;
  timelineRefreshToken?: number;

  contactFlowStatus: { [key: string]: any };
  isLoadingFlowStatus: boolean;
  flowProgress?: any;
  onToggleFlow: (flowType: string, isPaused: boolean, reason?: string) => void;

  pendingTasksCount?: number;
  hasFlowAlert?: boolean;

  funnelStage?: FunnelStage;
  funnelStatus?: FunnelStatus;
  leadId?: number | null;
  onLeadUpdated?: (updated: Lead) => void;
  onFunnelChanged?: () => void;

  contactTags: TagResponse[];
  tagsLoading: boolean;
  onUpdateTags: (tagIds: number[]) => void;
}

const TABS: Array<{ key: ContactSidebarTab; label: string; icon: any }> = [
  { key: 'timeline', label: 'Histórico', icon: Clock },
  { key: 'tasks', label: 'Tarefas', icon: CheckCircle2 },
  { key: 'notes', label: 'Anotações', icon: StickyNote },
  { key: 'flows', label: 'Fluxos', icon: Activity },
];

const ContactSidebar: React.FC<ContactSidebarProps> = ({
  isOpen,
  attendanceEnabled = false,
  onClose,
  contactId,
  contactPhone,
  contactName,
  contactPhoto,
  contactSourceId,
  clinicId,
  humanMode,
  identityResolved,
  onModeConfirmed,
  initialTab = 'timeline',
  timelineRefreshToken = 0,
  contactFlowStatus,
  isLoadingFlowStatus,
  flowsEnabled = true,
  flowProgress,
  onToggleFlow,
  pendingTasksCount,
  hasFlowAlert,
  funnelStage,
  funnelStatus,
  leadId,
  onLeadUpdated,
  onFunnelChanged,
  contactTags,
  tagsLoading,
  onUpdateTags,
}) => {
  const { isDark } = useTheme();
  const { ref: attendanceRef, summary: attendanceSummary } = useContactAttendanceSummary(contactId, isOpen && attendanceEnabled);
  const [activeTab, setActiveTab] = useState<ContactSidebarTab>(initialTab);
  const [showEditLead, setShowEditLead] = useState(false);
  const [showCreateLead, setShowCreateLead] = useState(false);
  const [showCreateAppt, setShowCreateAppt] = useState(false);
  const [showAttendance, setShowAttendance] = useState(false);
  const [showNoShow, setShowNoShow] = useState(false);
  const [showReschedule, setShowReschedule] = useState(false);
  const [showSale, setShowSale] = useState(false);
  const [timelineRefreshKey, setTimelineRefreshKey] = useState(0);
  const [modeTransitionBusy, setModeTransitionBusy] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setActiveTab(initialTab);
      console.log('[ContactSidebar] Aberto', { contactId, contactPhone, initialTab, funnelStage });
    }
  }, [isOpen, initialTab, contactId, contactPhone, funnelStage]);

  useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (document.querySelector('[data-contact-mode-dialog="true"]')) return;
      if (!modeTransitionBusy) onClose();
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, modeTransitionBusy, onClose]);

  if (!isOpen) return null;

  const agendamentoId = funnelStatus?.agendamento_id;
  const comparecimentoId = funnelStatus?.comparecimento_id;
  const hadNoShow = funnelStage === 'faltou';

  const handleActionCompleted = (label: string) => {
    console.log(`[ContactSidebar] ${label} concluído, atualizando timeline e contatos`);
    setTimelineRefreshKey((k) => k + 1);
    onFunnelChanged?.();
  };

  const handleTagsChange = (tagIds: number[]) => {
    console.log('[ContactSidebar] Tags alteradas, atualizando timeline', { tagIds });
    onUpdateTags(tagIds);
    setTimeout(() => setTimelineRefreshKey((k) => k + 1), 500);
  };

  return (
    <>
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40 transition-opacity duration-200 animate-in fade-in"
        onClick={modeTransitionBusy ? undefined : onClose}
        aria-hidden="true"
      />

      <aside
        className={`fixed right-0 top-0 bottom-0 z-50 w-full sm:w-[420px] shadow-xl flex flex-col animate-in slide-in-from-right duration-200 ${
          isDark ? 'bg-[#111827] text-gray-200' : 'bg-white text-gray-800'
        }`}
        role="dialog"
        aria-label="Painel do contato"
      >
        <div
          className={`relative flex-shrink-0 ${
            isDark
              ? 'bg-gradient-to-br from-[#111827] via-[#0f1c33] to-[#111827]'
              : 'bg-gradient-to-br from-sminy-primary/10 via-white to-white'
          }`}
        >
          <button
            onClick={onClose}
            disabled={modeTransitionBusy}
            title="Fechar (Esc)"
            className={`absolute right-3 top-3 flex min-h-11 min-w-11 items-center justify-center rounded-xl transition-colors ${
              modeTransitionBusy ? 'cursor-not-allowed opacity-40' : ''
            } ${
              isDark
                ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700/40'
                : 'text-gray-500 hover:text-gray-800 hover:bg-gray-100'
            }`}
          >
            <X className="h-5 w-5" />
          </button>

          <div className="px-4 pt-5 pb-4 flex items-start gap-3">
            <div className="relative flex-shrink-0">
              <ContactAvatar photo={contactPhoto} name={contactName} className="w-14 h-14 rounded-full" iconClassName="w-7 h-7" />
            </div>

            <div className="flex-1 min-w-0 pt-0.5 pr-10">
              <h2
                className={`text-base font-semibold tracking-tight truncate ${
                  isDark ? 'text-white' : 'text-gray-900'
                }`}
              >
                {contactName}
              </h2>
              <p className={`text-xs truncate mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                {contactPhone}
              </p>
              <div className="mt-2">
                <LeadActionsBar
                  funnelStage={funnelStage}
                  onEditLead={() => setShowEditLead(true)}
                  onCreateLead={() => setShowCreateLead(true)}
                  onSchedule={() => setShowCreateAppt(true)}
                  onReschedule={() => setShowReschedule(true)}
                  onMarkAttendance={() => setShowAttendance(true)}
                  onMarkNoShow={() => setShowNoShow(true)}
                  onMarkSale={() => setShowSale(true)}
                />
              </div>
            </div>
          </div>

          <div ref={attendanceRef} className="px-4 pb-4">
            <ContactModeControl
              contactId={contactId}
              contactLabel={contactName || contactPhone}
              humanMode={humanMode}
              identityResolved={identityResolved}
              onModeConfirmed={onModeConfirmed}
              onBusyChange={setModeTransitionBusy}
              variant="panel"
              attendanceSummary={attendanceSummary}
            />
          </div>
        </div>

        <ContactTagsPanel
          contactTags={contactTags}
          tagsLoading={tagsLoading}
          clinicId={clinicId}
          onUpdateTags={handleTagsChange}
        />

        <div
          className={`flex border-y flex-shrink-0 ${
            isDark ? 'border-gray-700 bg-[#111827]' : 'border-gray-200 bg-gray-50'
          }`}
        >
          {TABS.filter(tab => flowsEnabled || tab.key !== 'flows').map(({ key, label, icon: Icon }) => {
            const isActive = activeTab === key;
            const showTaskBadge = key === 'tasks' && pendingTasksCount && pendingTasksCount > 0;
            const showFlowBadge = key === 'flows' && hasFlowAlert;
            return (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`flex-1 flex flex-col items-center gap-0.5 py-2.5 text-xs font-medium transition-colors relative border-b-2 ${
                  isActive
                    ? isDark
                      ? 'text-sminy-primary bg-[#111827] border-sminy-primary'
                      : 'text-sminy-primary bg-white border-sminy-primary'
                    : isDark
                    ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700/40 border-transparent'
                    : 'text-gray-500 hover:text-gray-800 hover:bg-gray-100 border-transparent'
                }`}
              >
                <div className="relative">
                  <Icon className="h-4 w-4" />
                  {showTaskBadge && (
                    <span className="absolute -top-1.5 -right-2 min-w-[14px] h-3.5 px-1 rounded-full bg-sminy-primary text-white text-[9px] font-bold flex items-center justify-center ring-2 ring-white">
                      {pendingTasksCount! > 9 ? '9+' : pendingTasksCount}
                    </span>
                  )}
                  {showFlowBadge && (
                    <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-red-500 ring-2 ring-white" />
                  )}
                </div>
                <span>{label}</span>
              </button>
            );
          })}
        </div>

        <div
          className={`flex-1 overflow-y-auto ${
            isDark ? 'bg-[#111827]' : 'bg-gradient-to-b from-gray-50 to-white'
          }`}
        >
          {(activeTab === 'timeline' || (!flowsEnabled && activeTab === 'flows')) && (
            <LeadTimeline
              key={`${timelineRefreshKey}-${contactId}`}
              contactId={contactId}
              contactPhone={contactPhone}
              clinicId={clinicId}
              leadId={leadId}
              refreshToken={timelineRefreshToken}
            />
          )}
          {activeTab === 'tasks' && (
            <SidebarTasksList
              recordId={contactId}
              contactId={contactPhone}
              contactPhone={contactPhone}
              clinicId={clinicId}
            />
          )}
          {activeTab === 'notes' && (
            <SidebarNotesList
              recordId={contactId}
              contactId={contactPhone}
              contactPhone={contactPhone}
              clinicId={clinicId}
            />
          )}
          {flowsEnabled && activeTab === 'flows' && (
            <ContactFlowControl
              flowStatus={contactFlowStatus}
              flowProgress={flowProgress}
              isLoading={isLoadingFlowStatus}
              onToggle={onToggleFlow}
            />
          )}
        </div>
      </aside>

      <EditLeadModal
        isOpen={showEditLead}
        onClose={() => setShowEditLead(false)}
        leadId={leadId}
        contactPhone={contactPhone}
        contactName={contactName}
        clinicId={clinicId}
        onSaved={(updated) => {
          setTimelineRefreshKey((k) => k + 1);
          onLeadUpdated?.(updated);
        }}
      />

      <CreateLeadModal
        isOpen={showCreateLead}
        onClose={() => setShowCreateLead(false)}
        contactPhone={contactPhone}
        contactName={contactName}
        clinicId={clinicId}
        onCreated={(created) => {
          console.log('[ContactSidebar] Lead criado, atualizando timeline e funil', created);
          setTimelineRefreshKey((k) => k + 1);
          onLeadUpdated?.(created);
          onFunnelChanged?.();
        }}
      />

      <CreateAppointmentModal
        isOpen={showCreateAppt}
        onClose={() => setShowCreateAppt(false)}
        leadId={leadId}
        contactName={contactName}
        contactPhone={contactPhone}
        contactSourceId={contactSourceId}
        clinicId={clinicId}
        onCreated={() => handleActionCompleted('Agendamento criado')}
      />

      <AttendanceModal
        isOpen={showAttendance}
        onClose={() => setShowAttendance(false)}
        leadId={leadId}
        agendamentoId={agendamentoId}
        contactName={contactName}
        contactPhone={contactPhone}
        clinicId={clinicId}
        onCreated={() => handleActionCompleted('Comparecimento registrado')}
      />

      <NoShowModal
        isOpen={showNoShow}
        onClose={() => setShowNoShow(false)}
        agendamentoId={agendamentoId}
        clinicId={clinicId}
        onMarked={() => handleActionCompleted('Falta registrada')}
      />

      <RescheduleModal
        isOpen={showReschedule}
        onClose={() => setShowReschedule(false)}
        agendamentoId={agendamentoId}
        agendamentoConsultaData={funnelStatus?.agendamento_consulta_data}
        hadNoShow={hadNoShow}
        clinicId={clinicId}
        onRescheduled={() => handleActionCompleted('Reagendamento concluído')}
      />

      <SaleModal
        isOpen={showSale}
        onClose={() => setShowSale(false)}
        leadId={leadId}
        comparecimentoId={comparecimentoId}
        contactName={contactName}
        contactPhone={contactPhone}
        clinicId={clinicId}
        onCreated={() => handleActionCompleted('Venda registrada')}
      />
    </>
  );
};

export default ContactSidebar;
